import React, { useState, useEffect } from "react";

interface Option {
  value: string;
  name: string;
}

interface RadioButtonControlProps {
  options: Option[];
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  required?: boolean;
  className?: string;
}

const RadioButtonControl: React.FC<RadioButtonControlProps> = ({
  options,
  name,
  value,
  onChange,
  required = false,
  className = "",
}) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(value);

  // Update internal state when the value prop changes
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Handle option selection
  const handleChange = (newValue: string) => {
    setSelectedValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <div
      className={`bg-white rounded border border-[#d0d2cc] flex flex-col ${className}`}
    >
      {/* Hidden input for form submission */}
      <input
        type="hidden"
        name={name}
        value={selectedValue || ""}
        required={required}
      />

      {options.map((option, index) => {
        const isSelected = selectedValue === option.value;

        // Apply rounded corners to the first and last items
        const borderRadiusClasses =
          index === 0
            ? "rounded-t-lg"
            : index === options.length - 1
              ? "rounded-b-lg"
              : "";

        // Apply bottom border except for the last item
        const borderBottomClass =
          index < options.length - 1 ? "border-b border-[#d0d2cc]" : "";

        return (
          <div
            key={option.value}
            className={`flex items-center p-4 cursor-pointer ${borderRadiusClasses} ${borderBottomClass} ${
              isSelected ? "border-2 border-[#122427]" : "border-transparent"
            }`}
            onClick={() => handleChange(option.value)}
            role="radio"
            aria-checked={isSelected}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleChange(option.value);
              }
            }}
          >
            <div className="flex items-center flex-shrink-0">
              <div
                className={`w-5 h-5 flex items-center justify-center rounded-full ${
                  isSelected
                    ? "border-2 border-[#122427]"
                    : "border border-[#d0d2cc]"
                }`}
              >
                {isSelected && (
                  <div className="w-2.5 h-2.5 bg-[#122427] rounded-full"></div>
                )}
              </div>
            </div>
            <div className="ml-3">
              <span
                className={`text-sm text-[#122427] ${
                  isSelected ? "font-semibold" : "font-medium"
                }`}
              >
                {option.name}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RadioButtonControl;
